// Dashboard: nøgletal for en periode – omsætning, bookinger, mersalg, medlemmer, gebyrer, betalingsmetoder og bestsellere.
// Ren beregning ud fra data (samme kode bruges i testversionen og i drift). Beløb i øre.
// To visninger (0.48.0): 'regnskab' = alle beløb ekskl. moms efter datoen for den udførte opgave; 'likviditet' = inkl. moms, og
// indbetalingerne følger betalingsdatoen (likviditetRaa her og dashboard_likviditet i databasen regner det samme).
import type { Arbejdstid, Booking, Fravaer, Medlemskab, SaerligDag, Steamer } from './typer.ts';
import { plusDage, ugedagMandag0 } from './dato.ts';
import { arbejdstidPaa } from './ledige.ts';
import { MOMS_PROCENT, moms as momsAf } from './pris.ts';
import { hovedstolAf } from './rykker.ts';

export type Visning = 'regnskab' | 'likviditet';
export type DashBetaling = { booking_id: number | null; ordre_id?: number | null; medlemskab_id: string | null; metode: string; beloeb_inkl: number; status: string; betalt: string | null };
/** Indbetalinger (status betalt) pr. betalingsdag og -måde med momsen i dem. */
export type LikvDag = { dato: string; metode: string; antal: number; beloeb: number; moms: number };
/** Likviditetens rådata: indbetalinger i perioden, udestående ved periodens slut (heraf hos rykker – trin 2+) og forventet ind
 *  de næste 30 dage fra i dag (heraf medlemsbesøg, der trækkes). */
export type LikvRaa = { dage: LikvDag[]; udestaaende: number; hosRykker: number; forventet: number; forventetMedlem: number };
export type Likviditet = { indbetalt: number; moms: number; udestaaende: number; hosRykker: number; forventet: number; forventetMedlem: number };
export const BETALINGSMAADER = ['mobilepay', 'viva_kort', 'viva_abonnement', 'faktura'] as const;
export type DashMedlemskab = Pick<Medlemskab, 'id' | 'kunde_id' | 'start' | 'status' | 'besoeg_pr_aar' | 'pris_pr_besoeg_inkl' | 'navn'> & { oprettet?: string | null; opsagt?: string | null };

export type Spand = 'dag' | 'uge' | 'maaned';
/** brands: omsætning ekskl. moms pr. brand ('' = virksomhedens eget navn). */
/** omsaetning og mersalg følger visningen (ekskl. eller inkl. moms); indbetalt er inkl. moms efter betalingsdato. */
export type DashPunkt = { fra: string; etiket: string; omsaetning: number; mersalg: number; bookinger: number; medlemmer: number; brands: Record<string, number>; indbetalt: number; indbetaltMoms: number };
/** En kundesag (reklamation) – tælles pr. steamer. */
export type DashSag = { steamer_id: string | null; oprettet: string };
/** Steamernes arbejdstid – bruges til udnyttelse (bookede minutter / minutter på arbejde). */
export type DashKapacitet = { steamere: Steamer[]; arbejdstider: Arbejdstid[]; fravaer: Fravaer[]; saerligeDage: SaerligDag[]; lukkedage: string[] };
/** Alle beløb følger visningen: ekskl. moms (regnskab) eller inkl. moms (likviditet). omsaetningEkskl og momsUdfoert er altid det, de hedder. */
export type Dashboard = {
  fra: string; til: string; spand: Spand; visning: Visning;
  omsaetning: number; omsaetningEkskl: number; momsUdfoert: number;   // udførte opgaver i perioden
  planlagt: number;                                      // godkendte, endnu ikke udførte opgaver i perioden
  likviditet: Likviditet;
  bookinger: { i_alt: number; faerdige: number; aflyste: number; medlem: number; enkelt: number; aflystAndel: number | null };
  mersalg: { beloeb: number; antal: number; tilbudt: number; rate: number | null };
  gns: { booking: number | null; bookingUdenMersalg: number | null; medlemsbesoeg: number | null; medlemsbesoegUdenMersalg: number | null; medlemAar: number | null };
  medlemmer: { aktive: number; vedStart: number; nye: number; opsagte: number; churn: number | null; churnMd: number | null; interval: { besoeg_pr_aar: number; antal: number }[] };   // churn = opsagte / alle, der var medlem i perioden
  gebyrer: { weekend: number; helligdag: number; faktura: number; flyt: number; aflys: number };
  betalinger: { metode: string; antal: number; beloeb: number }[];
  bestsellere: { id: string; navn: string; antal: number; beloeb: number }[];
  tilvalg: { id: string; navn: string; antal: number; beloeb: number }[];
  steamere: { id: string; navn: string; opgaver: number; omsaetning: number; mersalg: number; gns: number | null; reklamationer: number; udnyttelse: number | null }[];
  udnyttelse: number | null;   // bookede minutter / minutter på arbejde (null = arbejdstider ukendt)
  brands: string[];            // brands med opgaver i perioden ('' = virksomhedens eget)
  historik: DashPunkt[];
};

const UDFOERT: Booking['status'][] = ['faerdig'];
const AKTIV_OPGAVE: Booking['status'][] = ['godkendt', 'i_gang', 'faerdig'];
const godkendteEkstra = (b: Booking) => (b.ekstra ?? []).filter((e) => e.status === 'godkendt');
const gns = (sum: number, n: number) => (n > 0 ? Math.round(sum / n) : null);
const dage = (fra: string, til: string) => Math.round((Date.parse(til) - Date.parse(fra)) / 864e5) + 1;
const MD = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

/** Hvor fint historikken deles op: dage op til 31 dage, uger op til ca. 4 måneder, ellers måneder. */
export function spandFor(fra: string, til: string): Spand { const n = dage(fra, til); return n <= 31 ? 'dag' : n <= 126 ? 'uge' : 'maaned'; }
/** Starten på de spande, perioden dækker. */
export function spande(fra: string, til: string, s: Spand): string[] {
  const ud: string[] = [];
  if (s === 'dag') for (let d = fra; d <= til; d = plusDage(d, 1)) ud.push(d);
  else if (s === 'uge') { const u = ugedagMandag0(fra); for (let d = plusDage(fra, -u); d <= til; d = plusDage(d, 7)) ud.push(d < fra ? fra : d); }
  else { let [y, m] = fra.split('-').map(Number); for (;;) { const d = `${y}-${String(m).padStart(2, '0')}-01`; if (d > til) break; ud.push(d < fra ? fra : d); m++; if (m > 12) { m = 1; y++; } } }
  return ud;
}
function etiket(d: string, s: Spand) {
  const [, m, dd] = d.split('-').map(Number);
  return s === 'maaned' ? `${MD[m - 1]} ${d.slice(2, 4)}` : `${dd}/${m}`;
}
/** Momsen i en indbetaling: bookingernes egen momsandel (moms / total), så momsfri gebyrer (faktura, rykker, renter) ikke giver moms.
 *  Kendes ingen booking (fx et træk uden booking), regnes med fuld moms (25 %: beløb − beløb / 1,25) – en tilnærmelse, som
 *  dashboard_likviditet i databasen bruger på samme måde. */
export function momsAfIndbetaling(beloeb: number, bk: readonly { total: number; moms: number }[]): number {
  const t = bk.reduce((s, b) => s + b.total, 0), m = bk.reduce((s, b) => s + b.moms, 0);
  return t > 0 ? Math.round((beloeb * m) / t) : beloeb - Math.round(beloeb / (1 + MOMS_PROCENT / 100));
}

/** Passer bookingen til steamer- og brandfilteret? */
const filter = (st: string | null, br: Set<string> | null) => (b: Booking) => (!st || b.steamer_id === st) && (!br || br.has(b.brand_id ?? ''));

/**
 * Likviditetens rådata ud fra alle data (testversionen; drift: dashboard_likviditet). Indbetalinger med status betalt og
 * betalingsdato i perioden – med filter følger de bookingen (eller ordrens bookinger). Udestående: rigtige rykkersager
 * (`rykkerSag` – samme afgrænsning som rykkerlisten, ikke importerede bookinger eller partnerfaktura) udført senest periodens
 * slut, i hovedstol (uden rykkergebyrer og renter); hosRykker = dem, der har fået en rykker eller er hos inkasso.
 * Forventet: godkendte/igangværende, ubetalte opgaver de næste 30 dage fra `idag` (inkl. medlemsbesøg, der trækkes på dagen).
 */
export function likviditetRaa(a: {
  bookinger: Booking[]; betalinger: DashBetaling[]; fra: string; til: string; idag: string; steamer_id?: string | null; brand_ids?: string[] | null;
  rykkerSag: (b: Booking) => boolean; hosRykker: (b: Booking) => boolean;
}): LikvRaa {
  const st = a.steamer_id ?? null, br = a.brand_ids?.length ? new Set(a.brand_ids) : null, passer = filter(st, br);
  const byId = new Map(a.bookinger.map((b) => [b.id, b]));
  const pOrdre = new Map<number, Booking[]>();
  for (const b of a.bookinger) if (b.ordre_id != null) pOrdre.set(b.ordre_id, [...(pOrdre.get(b.ordre_id) ?? []), b]);
  const dage = new Map<string, LikvDag>();
  for (const p of a.betalinger) {
    if (p.status !== 'betalt' || !p.betalt) continue;
    const d = p.betalt.slice(0, 10);
    if (d < a.fra || d > a.til) continue;
    const bk = p.booking_id != null ? [byId.get(p.booking_id)].filter((b): b is Booking => !!b) : p.ordre_id != null ? pOrdre.get(p.ordre_id) ?? [] : [];
    if ((st || br) && !bk.some(passer)) continue;
    const n = d + '|' + p.metode, x = dage.get(n) ?? { dato: d, metode: p.metode, antal: 0, beloeb: 0, moms: 0 };
    x.antal++; x.beloeb += p.beloeb_inkl; x.moms += momsAfIndbetaling(p.beloeb_inkl, bk); dage.set(n, x);
  }
  const ude = a.bookinger.filter((b) => a.rykkerSag(b) && b.dato <= a.til && passer(b));
  const slut = plusDage(a.idag, 29);
  const frem = a.bookinger.filter((b) => (b.status === 'godkendt' || b.status === 'i_gang') && !b.betalt && b.total > 0 && b.dato >= a.idag && b.dato <= slut && passer(b));
  return {
    dage: [...dage.values()].sort((p, q) => p.dato.localeCompare(q.dato) || p.metode.localeCompare(q.metode)),
    udestaaende: ude.reduce((s, b) => s + hovedstolAf(b), 0), hosRykker: ude.filter(a.hosRykker).reduce((s, b) => s + hovedstolAf(b), 0),
    forventet: frem.reduce((s, b) => s + b.total, 0), forventetMedlem: frem.filter((b) => b.medlemskab_id).reduce((s, b) => s + b.total, 0),
  };
}

/** Var medlemskabet aktivt på datoen? */
export function medlemPaa(m: DashMedlemskab, dato: string) {
  const fra = m.oprettet?.slice(0, 10) ?? m.start;
  if (fra > dato) return false;
  if (m.status === 'opsagt') return !!m.opsagt && m.opsagt.slice(0, 10) > dato;
  return true;
}

export function beregnDashboard(a: {
  bookinger: Booking[]; medlemskaber: DashMedlemskab[];
  visning?: Visning;            // standard: likviditet (inkl. moms)
  likv?: LikvRaa;               // likviditetens rådata (drift: dashboard_likviditet; testversionen: likviditetRaa)
  betalinger?: DashBetaling[];  // uden likv: indbetalingerne regnes herfra (kun pr. dag – udestående og forventet bliver 0)
  ydelser: { id: string; navn: string }[]; tilvalg: { id: string; navn: string }[]; steamere: { id: string; navn: string }[];
  fra: string; til: string; steamer_id?: string | null;
  brand_ids?: string[] | null;  // null/tom = alle brands; ellet et eller flere ('' = virksomhedens eget)
  skadesager?: DashSag[]; kapacitet?: DashKapacitet;
}): Dashboard {
  const { fra, til } = a;
  const visning = a.visning ?? 'likviditet';
  const ekskl = visning === 'regnskab';
  const st = a.steamer_id ?? null;
  const br = a.brand_ids?.length ? new Set(a.brand_ids) : null;
  const iDatoer = a.bookinger.filter((b) => b.dato >= fra && b.dato <= til && (!st || b.steamer_id === st));
  const iPer = br == null ? iDatoer : iDatoer.filter((b) => br.has(b.brand_id ?? ''));
  const udf = iPer.filter((b) => UDFOERT.includes(b.status));
  const navnY = (id: string) => a.ydelser.find((y) => y.id === id)?.navn ?? id;
  const navnT = (id: string) => a.tilvalg.find((y) => y.id === id)?.navn ?? id;
  // Beløb i visningen: bookingens pris er ekskl. moms (momsfri gebyrer som fakturagebyret står uden moms og trækkes ikke fra)
  const bel = (b: Booking) => (ekskl ? b.pris : b.total);
  const mersalgAf = (b: Booking) => godkendteEkstra(b).reduce((s, e) => s + (ekskl ? e.beloeb : e.total), 0);
  const tilvalgBel = (pris: number) => (ekskl ? pris : pris + momsAf(pris));   // tilvalgets pris står ekskl. moms
  const likv = a.likv ?? likviditetRaa({ bookinger: a.bookinger, betalinger: a.betalinger ?? [], fra, til, idag: til, steamer_id: st, brand_ids: a.brand_ids, rykkerSag: () => false, hosRykker: () => false });

  const omsaetning = udf.reduce((s, b) => s + bel(b), 0);
  const omsaetningEkskl = udf.reduce((s, b) => s + b.pris, 0);
  const momsUdfoert = udf.reduce((s, b) => s + b.total - b.pris, 0);
  const planlagt = iPer.filter((b) => b.status === 'godkendt' || b.status === 'i_gang').reduce((s, b) => s + bel(b), 0);

  // Mersalg (tilvalg solgt efter bestillingen) på opgaver i perioden
  let msBeloeb = 0, msAntal = 0, msTilbudt = 0, msAfvist = 0;
  for (const b of iPer) for (const e of b.ekstra ?? []) {
    if (e.status === 'annulleret') continue;
    msTilbudt++;
    if (e.status === 'godkendt') { msAntal++; msBeloeb += ekskl ? e.beloeb : e.total; } else if (e.status === 'afvist') msAfvist++;
  }

  const medlemsUdf = udf.filter((b) => b.medlemskab_id);
  const gebyrer = { weekend: 0, helligdag: 0, faktura: 0, flyt: 0, aflys: 0 };
  for (const b of iPer) if (b.status !== 'aflyst' || (b.gebyrer ?? []).some((g) => g.type === 'aflys'))
    for (const g of b.gebyrer ?? []) if (g.type in gebyrer) gebyrer[g.type as keyof typeof gebyrer] += g.beloeb + (ekskl ? 0 : g.moms);   // rykkerlinjer tælles ikke her

  // Medlemmer (ikke pr. steamer – et medlemskab hører til kunden)
  const ms = a.medlemskaber;
  const aktiveVedSlut = ms.filter((m) => medlemPaa(m, til));
  const vedStart = ms.filter((m) => medlemPaa(m, plusDage(fra, -1))).length;
  const nye = ms.filter((m) => { const d = (m.oprettet?.slice(0, 10) ?? m.start); return d >= fra && d <= til; }).length;
  const opsagte = ms.filter((m) => m.status === 'opsagt' && m.opsagt && m.opsagt.slice(0, 10) >= fra && m.opsagt.slice(0, 10) <= til).length;
  // Medlemsprisen står inkl. moms; ekskl. regnes med fuld moms (medlemsbesøg har ingen momsfri dele)
  const medlemAarInkl = gns(aktiveVedSlut.reduce((s, m) => s + m.pris_pr_besoeg_inkl * m.besoeg_pr_aar, 0), aktiveVedSlut.length);
  const medlemAar = medlemAarInkl == null || !ekskl ? medlemAarInkl : Math.round(medlemAarInkl / (1 + MOMS_PROCENT / 100));
  const interval = new Map<number, number>();
  for (const m of aktiveVedSlut) interval.set(m.besoeg_pr_aar, (interval.get(m.besoeg_pr_aar) ?? 0) + 1);

  // Betalinger modtaget i perioden pr. metode (efter betalingsdato; følger bookingens steamer og brand, når der filtreres)
  const metoder = new Map<string, { antal: number; beloeb: number }>();
  for (const p of likv.dage) { const x = metoder.get(p.metode) ?? { antal: 0, beloeb: 0 }; x.antal += p.antal; x.beloeb += ekskl ? p.beloeb - p.moms : p.beloeb; metoder.set(p.metode, x); }

  // Bestsellere: pakker og tilvalg på godkendte og udførte opgaver
  const best = new Map<string, { antal: number; beloeb: number }>();
  const tv = new Map<string, { antal: number; beloeb: number }>();
  for (const b of iPer.filter((x) => AKTIV_OPGAVE.includes(x.status))) {
    const y = best.get(b.ydelse_id) ?? { antal: 0, beloeb: 0 }; y.antal++; y.beloeb += bel(b); best.set(b.ydelse_id, y);
    const alle = [...b.tilvalg, ...godkendteEkstra(b).flatMap((e) => e.tilvalg)];
    for (const t of alle) { const x = tv.get(t.tilvalg_id) ?? { antal: 0, beloeb: 0 }; x.antal += t.antal; x.beloeb += tilvalgBel(t.pris); tv.set(t.tilvalg_id, x); }
  }
  const sorter = (m: Map<string, { antal: number; beloeb: number }>, navn: (id: string) => string) =>
    [...m].map(([id, x]) => ({ id, navn: navn(id), ...x })).sort((p, q) => q.antal - p.antal || q.beloeb - p.beloeb);

  // Udnyttelse: bookede minutter (valgte brands) mod minutter på arbejde, pr. steamer og samlet
  const kap = a.kapacitet;
  const tid = new Map<string, { booket: number; arbejde: number }>();
  if (kap) for (const s of kap.steamere.filter((x) => !st || x.id === st)) {
    let arbejde = 0;
    for (let d = fra; d <= til; d = plusDage(d, 1)) {
      const at = arbejdstidPaa({ steamer: s, dato: d, arbejdstider: kap.arbejdstider, fravaer: kap.fravaer, saerligeDage: kap.saerligeDage, lukkedage: kap.lukkedage });
      if (at) arbejde += at.slut - at.start;
    }
    const booket = iPer.filter((b) => b.steamer_id === s.id && AKTIV_OPGAVE.includes(b.status) && b.start != null && b.slut != null).reduce((x, b) => x + (b.slut! - b.start!), 0);
    tid.set(s.id, { booket, arbejde });
  }
  const andel = (t?: { booket: number; arbejde: number }) => (t && t.arbejde > 0 ? Math.min(1, t.booket / t.arbejde) : null);
  const samlet = [...tid.values()].reduce((x, t) => ({ booket: x.booket + t.booket, arbejde: x.arbejde + t.arbejde }), { booket: 0, arbejde: 0 });
  const sager = (a.skadesager ?? []).filter((s) => s.oprettet.slice(0, 10) >= fra && s.oprettet.slice(0, 10) <= til);

  // Pr. steamer
  const steamere = a.steamere.filter((s) => !st || s.id === st).map((s) => {
    const l = udf.filter((b) => b.steamer_id === s.id);
    const om = l.reduce((x, b) => x + bel(b), 0);
    return { id: s.id, navn: s.navn, opgaver: l.length, omsaetning: om, mersalg: l.reduce((x, b) => x + mersalgAf(b), 0), gns: gns(om, l.length),
      reklamationer: sager.filter((x) => x.steamer_id === s.id).length, udnyttelse: andel(tid.get(s.id)) };
  }).filter((s) => s.opgaver > 0 || st).sort((p, q) => q.omsaetning - p.omsaetning);

  // Historik
  const spand = spandFor(fra, til);
  const st0 = spande(fra, til, spand);
  const historik: DashPunkt[] = st0.map((s, i) => {
    const slut = i + 1 < st0.length ? plusDage(st0[i + 1], -1) : til;
    const l = udf.filter((b) => b.dato >= s && b.dato <= slut);
    const brands: Record<string, number> = {};
    for (const b of l) brands[b.brand_id ?? ''] = (brands[b.brand_id ?? ''] ?? 0) + b.pris;   // altid ekskl. moms
    const ind = likv.dage.filter((p) => p.dato >= s && p.dato <= slut);
    return { fra: s, etiket: etiket(s, spand), omsaetning: l.reduce((x, b) => x + bel(b), 0), mersalg: l.reduce((x, b) => x + mersalgAf(b), 0), bookinger: iPer.filter((b) => b.dato >= s && b.dato <= slut && b.status !== 'aflyst').length, medlemmer: ms.filter((m) => medlemPaa(m, slut)).length, brands,
      indbetalt: ind.reduce((x, p) => x + p.beloeb, 0), indbetaltMoms: ind.reduce((x, p) => x + p.moms, 0) };
  });

  const msUdf = udf.reduce((s, b) => s + mersalgAf(b), 0);
  const msMedlem = medlemsUdf.reduce((s, b) => s + mersalgAf(b), 0);
  const aktive = iPer.filter((b) => b.status !== 'aflyst').length;
  const aflyste = iPer.filter((b) => b.status === 'aflyst').length;
  const churn = vedStart + nye > 0 ? opsagte / (vedStart + nye) : null;
  return {
    fra, til, spand, visning, omsaetning, omsaetningEkskl, momsUdfoert, planlagt,
    likviditet: { indbetalt: likv.dage.reduce((s, p) => s + p.beloeb, 0), moms: likv.dage.reduce((s, p) => s + p.moms, 0),
      udestaaende: likv.udestaaende, hosRykker: likv.hosRykker, forventet: likv.forventet, forventetMedlem: likv.forventetMedlem },
    bookinger: { i_alt: aktive, faerdige: udf.length, aflyste, aflystAndel: aktive + aflyste > 0 ? aflyste / (aktive + aflyste) : null, medlem: iPer.filter((b) => b.medlemskab_id && b.status !== 'aflyst').length, enkelt: iPer.filter((b) => !b.medlemskab_id && b.status !== 'aflyst').length },
    mersalg: { beloeb: msBeloeb, antal: msAntal, tilbudt: msTilbudt, rate: msAntal + msAfvist > 0 ? msAntal / (msAntal + msAfvist) : null },
    gns: {
      booking: gns(omsaetning, udf.length), bookingUdenMersalg: gns(omsaetning - msUdf, udf.length),
      medlemsbesoeg: gns(medlemsUdf.reduce((s, b) => s + bel(b), 0), medlemsUdf.length), medlemsbesoegUdenMersalg: gns(medlemsUdf.reduce((s, b) => s + bel(b), 0) - msMedlem, medlemsUdf.length),
      medlemAar,
    },
    medlemmer: { aktive: aktiveVedSlut.length, vedStart, nye, opsagte, churn, churnMd: churn == null ? null : churn / Math.max(1, dage(fra, til) / (365.25 / 12)),
      interval: [...interval].map(([besoeg_pr_aar, antal]) => ({ besoeg_pr_aar, antal })).sort((p, q) => q.besoeg_pr_aar - p.besoeg_pr_aar) },
    gebyrer,
    betalinger: [...BETALINGSMAADER, ...[...metoder.keys()].filter((m) => !(BETALINGSMAADER as readonly string[]).includes(m)).sort()].map((m) => ({ metode: m, ...(metoder.get(m) ?? { antal: 0, beloeb: 0 }) })),
    bestsellere: sorter(best, navnY).slice(0, 8),
    tilvalg: sorter(tv, navnT).slice(0, 8),
    steamere,
    udnyttelse: andel(samlet.arbejde > 0 ? samlet : undefined),
    brands: [...new Set(iDatoer.map((b) => b.brand_id ?? ''))].sort(),
    historik,
  };
}
